import { calendarDay as localDate, dateTimeFormat as clock, daysBetween, deadlineWords } from "@nabla/api";

export { calendarDay as localDate, formatCountdown } from "@nabla/api";

export const saoPaulo = "America/Sao_Paulo";

const weekdays = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

export function formatDate(date: string, today: string): string {
  const [year, month, day] = date.split("-");
  return year === today.slice(0, 4) ? `${day}/${month}` : `${day}/${month}/${year}`;
}

const months = "janeiro fevereiro março abril maio junho julho agosto setembro outubro novembro dezembro".split(" ");

export function formatLongDate(date: string): string {
  const day = new Date(`${date}T12:00:00Z`);
  return `${weekdays[day.getUTCDay()] ?? ""}, ${day.getUTCDate()} de ${months[day.getUTCMonth()] ?? ""}`;
}

export function formatDayHeader(date: string, today: string): string {
  const ago = daysBetween(date, today);
  const name = ago === 0 ? "Hoje" : ago === 1 ? "Ontem" : ago > 1 && ago < 7 ? weekdays[new Date(`${date}T12:00:00Z`).getUTCDay()] : undefined;
  return name === undefined ? formatDate(date, today) : `${name}, ${formatDate(date, today)}`;
}

export function formatPeriod(from: string, to: string, today: string): string {
  return `${formatDate(from, today)}–${formatDate(to, today)}`;
}

export function formatTime(at: string, timezone: string): string {
  return clock("pt-BR", timezone, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(at));
}

export function formatStamp(at: string, now: string, timezone: string): string {
  const [day, today] = [localDate(at, timezone), localDate(now, timezone)];
  return day === today ? formatTime(at, timezone) : `${formatDate(day, today)} às ${formatTime(at, timezone)}`;
}

export function formatDayTime(at: string, now: string, timezone: string): string {
  const [day, today] = [localDate(at, timezone), localDate(now, timezone)];
  const time = formatTime(at, timezone);
  if (day === today) return `hoje às ${time}`;
  return daysBetween(day, today) === 1 ? `ontem às ${time}` : `${formatDate(day, today)} às ${time}`;
}

export function formatFreshness(at: string, now: string, timezone: string): string {
  const [day, today] = [localDate(at, timezone), localDate(now, timezone)];
  if (day === today) return formatTime(at, timezone);
  return daysBetween(day, today) === 1 ? `ontem ${formatTime(at, timezone)}` : formatDate(day, today);
}

export function formatAt(at: string, now: string, timezone: string): string {
  const [day, today] = [localDate(at, timezone), localDate(now, timezone)];
  if (day === today) return `às ${formatTime(at, timezone)}`;
  return daysBetween(day, today) === 1 ? `ontem às ${formatTime(at, timezone)}` : `em ${formatDate(day, today)} às ${formatTime(at, timezone)}`;
}

function sameMinute(from: string, to: string, timezone: string): boolean {
  return localDate(from, timezone) === localDate(to, timezone) && formatTime(from, timezone) === formatTime(to, timezone);
}

export function formatInterval(from: string, to: string, now: string, timezone: string): string {
  const today = localDate(now, timezone);
  const [start, end] = [localDate(from, timezone), localDate(to, timezone)];
  if (sameMinute(from, to, timezone)) return formatAt(to, now, timezone);
  if (start === today && end === today) return `entre ${formatTime(from, timezone)} e ${formatTime(to, timezone)}`;
  const name = (day: string) => (day === today ? "hoje" : daysBetween(day, today) === 1 ? "ontem" : formatDate(day, today));
  return `entre ${name(start)} ${formatTime(from, timezone)} e ${name(end)} ${formatTime(to, timezone)}`;
}

export function formatTimeRange(from: string, to: string, timezone: string): string {
  if (sameMinute(from, to, timezone)) return formatTime(to, timezone);
  const [start, end] = [localDate(from, timezone), localDate(to, timezone)];
  const at = (instant: string, day: string) => `${start === end ? "" : `${formatDate(day, end)} `}${formatTime(instant, timezone)}`;
  return `${at(from, start)}–${at(to, end)}`;
}

export function formatDeadline(at: string, now: string, timezone: string): string {
  return deadlineWords(at, now, timezone, formatTime(at, timezone), formatDate);
}

export function zoneName(timezone: string): string {
  if (timezone === "UTC") return "UTC";
  if (timezone === "America/Los_Angeles") return "horário do Pacífico";
  return `horário de ${timezone === saoPaulo ? "São Paulo" : (timezone.split("/").at(-1) ?? timezone).replaceAll("_", " ")}`;
}

export function zoneSuffix(timezone: string): string {
  return timezone === saoPaulo ? "" : `(${zoneName(timezone)})`;
}

export function formatDateTime(at: string, today: string, timezone: string): string {
  return `${formatDate(localDate(at, timezone), today)} ${formatTime(at, timezone)}`;
}
